import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { MessagesSquare, Shield, Users, Building2, LogOut, User as UserIcon } from "lucide-react";
import { useAuthStore } from "@/stores/auth";
import { authApi } from "@/api/auth";
import { useWS } from "@/hooks/useWS";
import { useWSStore } from "@/stores/ws";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import { Avatar } from "@/components/Avatar";
import { ProfileDialog } from "@/components/ProfileDialog";

const NAV_ITEMS = [
  { to: "/chat", label: "Chat", icon: MessagesSquare, roles: ["tenant_admin", "agent", "customer"] },
  { to: "/tenant/users", label: "Usuarios", icon: Users, roles: ["tenant_admin"] },
  { to: "/tenant/rooms", label: "Grupos", icon: Building2, roles: ["tenant_admin"] },
  { to: "/admin/tenants", label: "Empresas", icon: Shield, roles: ["superadmin"] },
];

export function Layout() {
  const me = useAuthStore((s) => s.me);
  const logout = useAuthStore((s) => s.logout);
  const wsStatus = useWSStore((s) => s.status);
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useWS();

  // cerrar dropdown clickeando afuera
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  if (!me) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">Cargando…</div>
    );
  }

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      /* sin red, igual cerramos */
    }
    logout();
    navigate("/login", { replace: true });
  };

  const items = NAV_ITEMS.filter((i) => i.roles.includes(me.role));
  const isMuted = me.tenant_status === "muted";

  return (
    <div className="flex h-full flex-col">
      {isMuted && (
        <div className="bg-amber-100 px-4 py-2 text-center text-sm text-amber-900">
          Cuenta pausada. Podés leer pero no enviar mensajes nuevos. Contactá al administrador.
        </div>
      )}
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-3 py-2 sm:px-4">
        <div className="flex min-w-0 items-center gap-1">
          <div className="mr-3 text-base font-bold text-brand-700">Datasup</div>
          <nav className="flex items-center gap-1 overflow-x-auto">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                className={({ isActive }) =>
                  cn(
                    "inline-flex shrink-0 items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium",
                    isActive ? "bg-brand-100 text-brand-700" : "text-slate-600 hover:bg-slate-100",
                  )
                }
              >
                <it.icon className="size-4" />
                <span className="hidden sm:inline">{it.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <ConnectionDot status={wsStatus} />
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 rounded-md p-1 hover:bg-slate-100"
              title="Menú de usuario"
            >
              <Avatar
                name={me.full_name}
                seed={me.id}
                size="sm"
                url={me.avatar_url ? `${me.avatar_url}&t=${Date.now()}` : undefined}
              />
              <div className="hidden text-right text-xs leading-tight sm:block">
                <div className="font-medium text-slate-900">{me.full_name}</div>
                <div className="text-slate-500">{me.email}</div>
              </div>
            </button>
            {menuOpen && (
              <div className="absolute right-0 z-10 mt-1 w-48 overflow-hidden rounded-md bg-white py-1 shadow-lg ring-1 ring-slate-200">
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setProfileOpen(true);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-100"
                >
                  <UserIcon className="size-4 text-slate-500" /> Mi perfil
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="size-4" /> Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <main className="min-h-0 flex-1 overflow-hidden bg-slate-50">
        <Outlet />
      </main>
      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
    </div>
  );
}

function ConnectionDot({ status }: { status: string }) {
  const color =
    status === "connected"
      ? "bg-emerald-500"
      : status === "connecting" || status === "reconnecting"
        ? "bg-amber-500"
        : "bg-slate-300";
  const label =
    status === "connected"
      ? "Conectado"
      : status === "connecting"
        ? "Conectando..."
        : status === "reconnecting"
          ? "Reconectando..."
          : "Sin conexión";
  return (
    <div className="flex items-center gap-1 text-xs text-slate-500" title={label}>
      <span className={cn("size-2 rounded-full", color)} />
      <span className="hidden sm:inline">{label}</span>
    </div>
  );
}
